import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { tigerApi } from '@/api/client';

/**
 * The org's cert workspace (a tenant in the controlplane). Provisioned lazily
 * via onboarding — the console mounts behind a gate on `useWorkspace`.
 */
export interface Workspace {
  id: string;
  name: string;
  slug: string;
  organization_id: string;
}

/**
 * Tiger (managed SSL) API — the @tiger controlplane cert inventory, called
 * directly (same shape as fastChannel: raw JSON, no `{data}` envelope; tenant
 * is implicit from the JWT org claim, so no tenant/serviceId in any path).
 *
 * This is the single source of truth for cert material + lifecycle. Every
 * service SSL surface (CDN/WAF/WAAP) picks from this inventory instead of
 * re-pasting PEM per vendor — see @tiger/docs/architecture.md §8.
 *
 * Endpoints (all under APP_TIGER_API_URL = …/api/v1):
 *   GET    /certs                 list / search
 *   GET    /certs/:id             one cert + current revision
 *   GET    /certs/:id/revisions   issue + renewal history
 *   GET    /certs/:id/bundle      raw material (gated on `reusable`)
 *   POST   /certs                 issue a new cert (internal LE)
 *   POST   /certs/import          upload an externally-issued bundle
 *   POST   /certs/:id/renew       force re-issue (async, internal_le only)
 *   DELETE /certs/:id             revoke (stops renewal)
 */

// ============================================
// Types  (mirror controlplane client/types.go — snake_case wire shape)
// ============================================

/** Lifecycle status of a cert request. */
export type CertStatus =
  | 'pending'
  | 'awaiting_dns'
  | 'issuing'
  | 'active'
  | 'failed'
  | 'revoking'
  | 'revoked';

/**
 * acme-dns DNS-01 delegation record, present on external-domain certs while
 * they are `awaiting_dns`. The customer adds this one-time CNAME at their DNS;
 * cert-manager then auto-issues + auto-renews forever.
 */
export interface Delegation {
  cname_host: string;
  cname_target: string;
}

/** How the cert entered the inventory. */
export type CertSource = 'internal_le' | 'imported' | 'shared_wildcard';

/** What produced a revision. */
export type RevisionTrigger = 'initial' | 'renewal' | 'forced';

/** One issuance event (initial + each renewal). */
export interface Revision {
  revision_number: number;
  fingerprint: string;
  issued_at: string;
  expires_at: string;
  deployed_at?: string;
  /** Edges the cert-uploader confirmed a push to: subset of {vedge, edgenext}. */
  deployed_to?: string[];
  trigger: RevisionTrigger;
}

/** A cert request (the inventory row). */
export interface Cert {
  id: string;
  dns_names: string[];
  status: CertStatus;
  source: CertSource;
  reusable: boolean;
  created_at: string;
  created_by: string;
  metadata?: Record<string, unknown>;
  current_revision?: Revision;
  revisions_count?: number;
  revoked_at?: string;
  /** Present for external domains in `awaiting_dns` — the CNAME to add once. */
  delegation?: Delegation;
}

/** Raw cert material from GET /certs/:id/bundle (only when `reusable`). */
export interface Bundle {
  cert_pem: string;
  chain_pem: string;
  key_pem: string;
  fullchain_pem: string;
}

/** Body for POST /certs — issue a new (internal LE) cert. */
export interface IssueCertInput {
  dns_names: string[];
  metadata?: Record<string, unknown>;
}

/** Body for POST /certs/import — upload an externally-issued bundle. */
export interface ImportCertInput {
  cert_pem: string;
  chain_pem?: string;
  key_pem: string;
  metadata?: Record<string, unknown>;
}

/** Optional filters for the list endpoint. */
export interface CertListFilter {
  domain?: string;
  status?: CertStatus;
  limit?: number;
}

/** One activity/audit entry (from auditz, proxied via /activity). */
export interface Activity {
  id: string;
  occurred_at: string;
  action: string; // issue | import | renew | revoke | recheck | download
  resource_type: string;
  resource_id: string;
  resource_name?: string;
  actor_id?: string;
  actor_email?: string;
  status: string;
  client_ip?: string;
  user_agent?: string;
  new_value?: unknown;
}

export interface ActivityFilter {
  resource_id?: string;
  action?: string;
  limit?: number;
}

// ============================================
// Query keys
// ============================================

export const tigerKeys = {
  all: ['tiger'] as const,
  workspace: ['tiger', 'workspace'] as const,
  certs: (filter?: CertListFilter) =>
    [...tigerKeys.all, 'certs', filter ?? {}] as const,
  cert: (id: string) => [...tigerKeys.all, 'cert', id] as const,
  revisions: (id: string) => [...tigerKeys.all, 'cert', id, 'revisions'] as const,
  bundle: (id: string) => [...tigerKeys.all, 'cert', id, 'bundle'] as const,
  activity: (filter?: ActivityFilter) =>
    [...tigerKeys.all, 'activity', filter ?? {}] as const,
};

// ============================================
// Helpers
// ============================================

/**
 * Turn literal `\n` sequences into real newlines. PEM pasted through some
 * inputs (or copied from JSON) arrives escaped; the API needs real newlines.
 * Mirrors the CDN upload path (src/api/manticore/domains.ts).
 */
export function normalizePEM(pem: string): string {
  return pem.includes('\\n') ? pem.replace(/\\n/g, '\n') : pem;
}

// ============================================
// Onboarding (workspace gate)
// ============================================

/**
 * GATE query — `GET /workspace`. Returns the workspace, or `null` when the org
 * is resolvable but not provisioned yet (→ show onboarding). Non-2xx surfaces as
 * a real error. `retry: false` so the gate decides fast.
 */
export function useWorkspace() {
  return useQuery<Workspace | null>({
    queryKey: tigerKeys.workspace,
    retry: false,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await tigerApi.get<{ workspace: Workspace | null }>('/workspace');
      return res.data?.workspace ?? null;
    },
  });
}

/** User-driven get-or-create (`POST /workspace`); flips the gate on success. */
export function useActivateWorkspace(
  options?: UseMutationOptions<Workspace, unknown, void>,
) {
  const qc = useQueryClient();
  return useMutation<Workspace, unknown, void>({
    mutationFn: async () => {
      const res = await tigerApi.post<{ workspace: Workspace }>('/workspace');
      return res.data.workspace;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: tigerKeys.workspace });
      qc.invalidateQueries({ queryKey: tigerKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

// ============================================
// Reads
// ============================================

export function useCerts(filter?: CertListFilter, opts?: { enabled?: boolean }) {
  return useQuery<Cert[]>({
    queryKey: tigerKeys.certs(filter),
    enabled: opts?.enabled ?? true,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await tigerApi.get<{ items: Cert[] }>('/certs', {
        params: filter,
      });
      return res.data?.items ?? [];
    },
  });
}

/** Tenant-scoped activity feed (proxied from auditz via the controlplane). */
export function useActivity(filter?: ActivityFilter) {
  return useQuery<Activity[]>({
    queryKey: tigerKeys.activity(filter),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    queryFn: async () => {
      const res = await tigerApi.get<{ items: Activity[] }>('/activity', {
        params: filter,
      });
      return res.data?.items ?? [];
    },
  });
}

/** Keyset cursor returned by /activity for the next page. */
interface ActivityCursor {
  before_time?: string;
  before_id?: string;
}

/** Paginated activity feed (keyset "load more") with an optional action filter. */
export function useActivityInfinite(opts?: { action?: string; limit?: number }) {
  const limit = opts?.limit ?? 25;
  return useInfiniteQuery({
    queryKey: [...tigerKeys.all, 'activity-inf', { action: opts?.action ?? '' }] as const,
    initialPageParam: null as ActivityCursor | null,
    staleTime: 10_000,
    queryFn: async ({ pageParam }) => {
      const res = await tigerApi.get<{ items: Activity[]; next: ActivityCursor }>('/activity', {
        params: { action: opts?.action || undefined, limit, ...(pageParam ?? {}) },
      });
      return res.data ?? { items: [], next: {} };
    },
    getNextPageParam: (last) =>
      last.items.length >= limit && last.next?.before_id ? last.next : undefined,
  });
}

export function useCertDetail(id: string | undefined) {
  return useQuery<Cert | null>({
    queryKey: tigerKeys.cert(id ?? ''),
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await tigerApi.get<Cert>(`/certs/${id}`);
      return res.data ?? null;
    },
  });
}

export function useCertRevisions(id: string | undefined) {
  return useQuery<Revision[]>({
    queryKey: tigerKeys.revisions(id ?? ''),
    enabled: !!id,
    staleTime: 30_000,
    queryFn: async () => {
      const res = await tigerApi.get<{ items: Revision[] }>(`/certs/${id}/revisions`);
      return res.data?.items ?? [];
    },
  });
}

/**
 * Fetch raw cert material. Not cached by default (`enabled: false` unless a
 * caller opts in) — key material should not linger in the query cache. Prefer
 * calling `refetch()` on demand (e.g. a "download bundle" action).
 */
export function useCertBundle(id: string | undefined, enabled = false) {
  return useQuery<Bundle>({
    queryKey: tigerKeys.bundle(id ?? ''),
    enabled: enabled && !!id,
    gcTime: 0,
    staleTime: 0,
    queryFn: async () => {
      const res = await tigerApi.get<Bundle>(`/certs/${id}/bundle`);
      return res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

/** Issue a new managed (internal LE) cert. */
export function useIssueCert(
  options?: UseMutationOptions<Cert, unknown, IssueCertInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueCertInput) => {
      try {
        const res = await tigerApi.post<Cert>('/certs', input);
        return res.data;
      } catch (e) {
        // Dedupe: when this tenant already holds an ACTIVE cert for the same
        // SAN set, the API returns 409 with the existing cert (it won't spend a
        // duplicate Let's Encrypt order). Adopt it — issuing a domain you
        // already have should idempotently hand back that cert, not error.
        if (isAxiosError(e) && e.response?.status === 409 && e.response.data) {
          return e.response.data as Cert;
        }
        throw e;
      }
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: tigerKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Upload an externally-issued bundle into the inventory. */
export function useImportCert(
  options?: UseMutationOptions<Cert, unknown, ImportCertInput>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ImportCertInput) => {
      const res = await tigerApi.post<Cert>('/certs/import', {
        ...input,
        cert_pem: normalizePEM(input.cert_pem),
        chain_pem: input.chain_pem ? normalizePEM(input.chain_pem) : undefined,
        key_pem: normalizePEM(input.key_pem),
      });
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: tigerKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Force cert-manager to re-issue now (async; poll the detail for the new revision). */
export function useRenewCert(
  options?: UseMutationOptions<Cert, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await tigerApi.post<Cert>(`/certs/${id}/renew`);
      return res.data;
    },
    ...options,
    onSuccess: (cert, ...rest) => {
      qc.invalidateQueries({ queryKey: tigerKeys.cert(cert.id) });
      qc.invalidateQueries({ queryKey: tigerKeys.certs() });
      options?.onSuccess?.(cert, ...rest);
    },
  });
}

/**
 * Recheck an `awaiting_dns` cert now — tells the backend to force cert-manager
 * to retry the ACME challenge immediately (after the customer adds the CNAME),
 * instead of waiting for the default retry backoff. 202, no body.
 */
export function useRecheckCert(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await tigerApi.post(`/certs/${id}/recheck`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: tigerKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Revoke a cert (stops renewal; terminal). */
export function useRevokeCert(
  options?: UseMutationOptions<void, unknown, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await tigerApi.delete(`/certs/${id}`);
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: tigerKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
